import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import {
  createMockExercise,
  createMockExerciseCategory,
  createMockWorkoutTemplate,
  duplicateMockWorkoutTemplate,
  getMockExercise,
  getMockExerciseCategories,
  getMockExercises,
  getMockWorkoutTemplate,
  getMockWorkoutTemplates,
  seedMockStarterExercises,
  setMockExerciseStatus,
  setMockWorkoutTemplateStatus,
  updateMockExercise,
  updateMockWorkoutTemplate,
} from './mock'
import type {
  CreateExerciseBody,
  Exercise,
  ExerciseCategory,
  ExerciseListParams,
  ExerciseStatus,
  SaveWorkoutTemplateBody,
  UpdateExerciseBody,
  WorkoutListResponse,
  WorkoutTemplate,
  WorkoutTemplateListParams,
  WorkoutTemplateStatus,
  WorkoutTemplateSummary,
} from './model'

export * from './model'
export { uploadExerciseMedia } from './upload'

/**
 * Workout domain service (exercise library + workout templates). Serves the
 * localStorage-backed mock only while `VITE_WORKOUT_USE_MOCK` is exactly
 * 'true' (backend endpoints do not exist yet), and the real `/workout/*` API
 * otherwise. Opt-in like the loyalty service, so a hosting target that
 * doesn't set the var never exposes the demo mock to real tenants — swapping
 * to the real backend touches only this file plus the env var.
 */
const USE_MOCK = import.meta.env.VITE_WORKOUT_USE_MOCK === 'true'

/**
 * Exposed for the auth context: while the mock runs, the feature stays
 * visible for demos regardless of the club-config flag (loyalty precedent).
 */
export const WORKOUT_USE_MOCK = USE_MOCK

export const WorkoutService = (client: AxiosInstance) => ({
  // ── Exercise categories ──────────────────────────────────────────────────
  async getExerciseCategories(): Promise<ExerciseCategory[]> {
    if (USE_MOCK) return getMockExerciseCategories()
    const { data } = await wrapApiCall<ExerciseCategory[]>(() =>
      client.get('/workout/exercise-categories')
    )
    return data
  },

  async createExerciseCategory(body: { name: string }): Promise<ExerciseCategory> {
    if (USE_MOCK) return createMockExerciseCategory(body)
    const { data } = await wrapApiCall<ExerciseCategory>(() =>
      client.post('/workout/exercise-categories', body)
    )
    return data
  },

  // ── Exercises ────────────────────────────────────────────────────────────
  async getExercises(params: ExerciseListParams = {}): Promise<WorkoutListResponse<Exercise>> {
    if (USE_MOCK) return getMockExercises(params)
    const { data } = await wrapApiCall<WorkoutListResponse<Exercise>>(() =>
      client.get('/workout/exercises', { params })
    )
    return data
  },

  async getExercise(id: number): Promise<Exercise> {
    if (USE_MOCK) return getMockExercise(id)
    const { data } = await wrapApiCall<Exercise>(() => client.get(`/workout/exercises/${id}`))
    return data
  },

  async createExercise(body: CreateExerciseBody): Promise<Exercise> {
    if (USE_MOCK) return createMockExercise(body)
    const { data } = await wrapApiCall<Exercise>(() => client.post('/workout/exercises', body))
    return data
  },

  async updateExercise(id: number, body: UpdateExerciseBody): Promise<Exercise> {
    if (USE_MOCK) return updateMockExercise(id, body)
    const { data } = await wrapApiCall<Exercise>(() =>
      client.patch(`/workout/exercises/${id}`, body)
    )
    return data
  },

  async setExerciseStatus(id: number, status: ExerciseStatus): Promise<Exercise> {
    if (USE_MOCK) return setMockExerciseStatus(id, status)
    const action = status === 'ARCHIVED' ? 'archive' : 'restore'
    const { data } = await wrapApiCall<Exercise>(() =>
      client.patch(`/workout/exercises/${id}/${action}`)
    )
    return data
  },

  async seedStarterExercises(): Promise<{ seeded: number }> {
    if (USE_MOCK) return seedMockStarterExercises()
    const { data } = await wrapApiCall<{ seeded: number }>(() =>
      client.post('/workout/exercises/seed-starter')
    )
    return data
  },

  // ── Workout templates ────────────────────────────────────────────────────
  async getWorkoutTemplates(
    params: WorkoutTemplateListParams = {}
  ): Promise<WorkoutListResponse<WorkoutTemplateSummary>> {
    if (USE_MOCK) return getMockWorkoutTemplates(params)
    const { data } = await wrapApiCall<WorkoutListResponse<WorkoutTemplateSummary>>(() =>
      client.get('/workout/templates', { params })
    )
    return data
  },

  async getWorkoutTemplate(id: number): Promise<WorkoutTemplate> {
    if (USE_MOCK) return getMockWorkoutTemplate(id)
    const { data } = await wrapApiCall<WorkoutTemplate>(() =>
      client.get(`/workout/templates/${id}`)
    )
    return data
  },

  async createWorkoutTemplate(body: SaveWorkoutTemplateBody): Promise<WorkoutTemplate> {
    if (USE_MOCK) return createMockWorkoutTemplate(body)
    const { data } = await wrapApiCall<WorkoutTemplate>(() =>
      client.post('/workout/templates', body)
    )
    return data
  },

  async updateWorkoutTemplate(id: number, body: SaveWorkoutTemplateBody): Promise<WorkoutTemplate> {
    if (USE_MOCK) return updateMockWorkoutTemplate(id, body)
    const { data } = await wrapApiCall<WorkoutTemplate>(() =>
      client.put(`/workout/templates/${id}`, body)
    )
    return data
  },

  async setWorkoutTemplateStatus(
    id: number,
    status: WorkoutTemplateStatus
  ): Promise<WorkoutTemplate> {
    if (USE_MOCK) return setMockWorkoutTemplateStatus(id, status)
    const { data } = await wrapApiCall<WorkoutTemplate>(() =>
      client.patch(`/workout/templates/${id}/status`, { status })
    )
    return data
  },

  async duplicateWorkoutTemplate(id: number): Promise<WorkoutTemplate> {
    if (USE_MOCK) return duplicateMockWorkoutTemplate(id)
    const { data } = await wrapApiCall<WorkoutTemplate>(() =>
      client.post(`/workout/templates/${id}/duplicate`)
    )
    return data
  },
})
